/**
 * Land on one candidate's card: expand its role, then scroll it into view.
 *
 * The Feedback tab grew this first, for the Overdue tab's "Go to decision". Now that every
 * actionable Overdue chip routes somewhere (see slaChipTarget), the same three tabs inside
 * 📅 Scheduling need it too — and three copies of a timeout and a `getElementById` would drift.
 *
 * The delay is not decoration: the accordion animates open, and an element inside a collapsed
 * `AccordionContent` has no layout for `scrollIntoView` to work with. Expanding is additive —
 * it never collapses what the recruiter opened themselves.
 *
 * **Keyboard focus moves too, not just the viewport.** The chip that was clicked belongs to the
 * Overdue tab and unmounts the moment the tab switches, so focus falls back to `document.body`
 * and a keyboard or screen-reader user is dumped at the top of a page they were told they had
 * been taken into. Each card target carries `tabIndex={-1}` for this — programmatically
 * focusable, never in the tab order.
 */
import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

export interface FocusTarget {
  postingId: string;
  opportunityId: string;
}

/** Matches the accordion's own open/close animation; measured, not guessed. */
const SETTLE_MS = 250;

export function useFocusCard(
  focus: FocusTarget | null,
  setOpen: Dispatch<SetStateAction<string[]>>
) {
  useEffect(() => {
    if (!focus) return;
    setOpen((cur) => (cur.includes(focus.postingId) ? cur : [...cur, focus.postingId]));
    const t = setTimeout(() => {
      const card = document.getElementById(`ta-card-${focus.opportunityId}`);
      if (!card) return;
      // preventScroll, then scroll deliberately: focus() alone jumps the card just barely into
      // view, and centring it is what makes the landing readable.
      card.focus({ preventScroll: true });
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, SETTLE_MS);
    return () => clearTimeout(t);
  }, [focus, setOpen]);
}
